import { useState, useCallback } from 'react';
import { LexAIService } from '../services/api';
import { EmailReply, EnhancedDashboardEmail } from '../types/api';

interface UseEmailOperationsProps {
  email: EnhancedDashboardEmail;
  onSendReply?: (reply: EmailReply) => void;
}

interface UseEmailOperationsReturn {
  // Summary state
  aiSummary: string;
  isGeneratingSummary: boolean;
  generateSummary: () => Promise<void>;

  // Reply state
  replyText: string;
  setReplyText: (text: string) => void;
  isGeneratingReply: boolean;
  isSendingReply: boolean;
  generateDraftReply: () => Promise<void>;
  handleSendReply: () => Promise<void>;
}

export const useEmailOperations = ({
  email,
  onSendReply
}: UseEmailOperationsProps): UseEmailOperationsReturn => {
  // Summary state
  const [aiSummary, setAiSummary] = useState<string>('');
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);

  // Reply state
  const [replyText, setReplyText] = useState('');
  const [isGeneratingReply, setIsGeneratingReply] = useState(false);
  const [isSendingReply, setIsSendingReply] = useState(false);

  const generateSummary = useCallback(async () => {
    if (aiSummary) {
      return;
    }

    setIsGeneratingSummary(true);
    try {
      const summary = await LexAIService.generateEmailSummary(email.id);

      // Parse JSON response if the orchestrator returns structured data
      let parsedSummary = summary;
      try {
        const jsonResponse = JSON.parse(summary);
        if (jsonResponse.response) {
          parsedSummary = jsonResponse.response;
        } else if (jsonResponse.summary) {
          parsedSummary = jsonResponse.summary;
        }
      } catch {
        // Not JSON, use the raw string as-is
      }

      setAiSummary(parsedSummary);
    } catch (error) {
      setAiSummary('Failed to generate summary. Please try again.');
    } finally {
      setIsGeneratingSummary(false);
    }
  }, [email.id, aiSummary]);

  const generateDraftReply = useCallback(async () => {
    setIsGeneratingReply(true);
    try {
      const draft = await LexAIService.generateEmailReply(email.id);
      setReplyText(draft);
    } catch (error) {
      setReplyText('Failed to generate draft reply. Please write your own reply.');
    } finally {
      setIsGeneratingReply(false);
    }
  }, [email.id]);

  const handleSendReply = useCallback(async () => {
    if (!replyText.trim()) return;

    const reply: EmailReply = {
      to: [email.senderEmail],
      subject: email.subject.startsWith('Re:') ? email.subject : `Re: ${email.subject}`,
      body: replyText,
      replyToMessageId: email.id
    };

    setIsSendingReply(true);
    try {
      const success = await LexAIService.sendEmailReply(reply);
      if (success && onSendReply) {
        onSendReply(reply);
      }
      // Reset the reply after sending
      setReplyText('');
    } catch (error) {
      // Error handled silently
    } finally {
      setIsSendingReply(false);
    }
  }, [email, replyText, onSendReply]);

  return {
    // Summary
    aiSummary,
    isGeneratingSummary,
    generateSummary,

    // Reply
    replyText,
    setReplyText,
    isGeneratingReply,
    isSendingReply,
    generateDraftReply,
    handleSendReply
  };
};
